<template>
  <div class="fg_diagramm" v-if="loaded">
    <div class="fourcol">
      <div class="col0">
        <h6>Quartal</h6>
        <Dropdown
          v-model="quartal"
          :options="quartalOptions"
          panelClass="hd_dropdown_panel"
        />
      </div>
      <div class="col1">
        <h6>Fachgruppenbezeichnung</h6>
        <Dropdown
          v-model="thema"
          :options="themaOptions"
          panelClass="hd_dropdown_panel"
        />
      </div>
      <div class="col2">
        <Button @click="$emit('download-pdf')">Als PDF herunterladen</Button>
      </div>
    </div>
    <div class="fg_maincol">
      <div class="fg_charts_wrapper">
        <div class="fg_top">
          <div class="chartwrapper_kennzahlen chart_wrapper">
            <Kennzahlen
              :data="kennzahlenData"
              id="chart_kennzahlen"
              :inPrint="inPrint"
              :inDownload="inDownload"
            />
          </div>
        </div>
        <div class="fg_middle">
           <div class="chartwrapper_honorarRlv chart_wrapper">
            <HonorarRlv
              :data="honorarRlvData"
              id="chart_honorarRlv"
              :quartal="quartal"
            />
          </div>
          <div class="chartwrapper_anteilLeist chart_wrapper">
            <AnteilLeist
              :data="anteilLeistData"
              id="chart_anteilLeist"
              :inPrint="inPrint"
              :inDownload="inDownload"
            />
          </div>
        </div>
        <div class="fg_bottom chartwrapper_fgbttm">
          <FgBttm
            :data="fgBttmData"
            id="chart_fgbttm"
            :quartalSplit="quartalSplit"
            :inPrint="inPrint"
            :inDownload="inDownload"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from "vue";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import Kennzahlen from "@/components/fg/Kennzahlen";
import HonorarRlv from "@/components/fg/HonorarRlv";
import AnteilLeist from "@/components/fg/AnteilLeist";
import FgBttm from "@/components/fg/FgBttm";
import useReadSheet, {
  mergeWithPrevYear,
  orderHonorarAufteilung,
} from "@/composables/useReadSheet.js";

export default {
  name: "HonorarDiagramm",
  props: ["file", "inPrint", "inDownload"],
  components: { Dropdown, Button, Kennzahlen, HonorarRlv, AnteilLeist, FgBttm },
  setup(props) {
    const sheetData = ref(null);
    const quartal = ref(null);
    const quartalSplit = ref(null);
    const quartalPrev = ref(null);
    const thema = ref(null);
    const themaOptions = ref([]);
    const quartalOptions = ref([]);
    const loaded = ref(false);
    const inPrint = ref(false);
    const inDownload = ref(false);

    const kennzahlenData = ref(null);
    const honorarRlvData = ref(null);
    const anteilLeistData = ref(null);
    const fgBttmData = ref(null);

    const loadData = async () => {
      const dataUp = await useReadSheet(
        props.file,
        [
          "Honorar",
          "Kennzahlen",
          "Honorar_RLV",
          "EGV_MGV",
          "Honoraraufteilung",
        ],
        ["Honorar:0", "Honorar:3"]
      );

      // Update refs
      sheetData.value = dataUp;
      quartal.value = dataUp.selectLists[0].reverse()[0];
      quartalSplit.value = quartal.value.split("-");
      quartalPrev.value =
        quartalSplit.value[0] - 1 + "-" + quartalSplit.value[1];
      thema.value = dataUp.selectLists[1][0];
      quartalOptions.value = Object.values(dataUp.selectLists[0]);
      themaOptions.value = dataUp.selectLists[1];
      loaded.value = true;

      //Update rows
      updateFilteredRows();
    };

    loadData();

    const updateFilteredRows = () => {
      quartalSplit.value = quartal.value.split("-");
      quartalPrev.value =
        quartalSplit.value[0] - 1 + "-" + quartalSplit.value[1];

      // Kennzahlen
      kennzahlenData.value = Object.values(
        sheetData.value.rowData.Kennzahlen.filter((v) => {
          if (v[0] == quartal.value && v[1] == thema.value) {
            return v;
          }
        })
      );

      // HonorarRlv
      honorarRlvData.value = {
        rlv: mergeWithPrevYear(
          quartal.value,
          thema.value,
          sheetData.value.rowData.HonorarRLV
        ),
        egv: mergeWithPrevYear(
          quartal.value,
          thema.value,
          sheetData.value.rowData.EGVMGV
        ),
      };

      // AnteilLeist
      anteilLeistData.value = Object.values(
        sheetData.value.rowData.Honoraraufteilung.filter((v) => {
          if (v[0] == quartal.value && v[4] == thema.value) {
            return v;
          }
        })
      );

      anteilLeistData.value = {
        auf: Object.values(
          sheetData.value.rowData.Honoraraufteilung.filter((v) => {
            if (v[0] == quartal.value && v[4] == thema.value) {
              return v;
            }
          })
        ),
        rlv: Object.values(
          sheetData.value.rowData.HonorarRLV.filter((v) => {
            if (v[0] == quartal.value && v[1] == thema.value) {
              return v;
            }
          })
        ),
      };

      //FgBttm
      fgBttmData.value = orderHonorarAufteilung(
        Object.values(
          sheetData.value.rowData.Honoraraufteilung.filter((v) => {
            if (v[0] == quartal.value && v[4] == thema.value) {
              return v;
            }
          })
        )
      );
    };

    watch(
      () => quartal.value,
      (val, prevVal) => {
        updateFilteredRows();
      }
    );
    watch(
      () => thema.value,
      (val, prevVal) => {
        updateFilteredRows();
      }
    );

    watch(
      () => props.inPrint,
      (val, prevVal) => {
        inPrint.value = val;
        // chartCenter.value.chartOptions.scales.y.ticks.display = props.inPrint;
        // chartRight.value.chartOptions.scales.y.ticks.display = props.inPrint;
      }
    );

    watch(
      () => props.inDownload,
      (val, prevVal) => {
        inDownload.value = props.inDownload;
        // if (val) {
        //   chartLeft.value.chartOptions.animation = false;
        //   chartCenter.value.chartOptions.animation = false;
        //   chartRight.value.chartOptions.animation = false;
        // } else {
        //   chartLeft.value.chartOptions.animation = true;
        //   chartCenter.value.chartOptions.animation = true;
        //   chartRight.value.chartOptions.animation = true;
        // }
      }
    );

    return {
      sheetData,
      kennzahlenData,
      honorarRlvData,
      anteilLeistData,
      fgBttmData,
      thema,
      quartal,
      quartalSplit,
      quartalPrev,
      themaOptions,
      quartalOptions,
      loaded,
      inPrint,
      inDownload,
    };
  },
};
</script>

<style lang="scss"></style>
